import { getSessionToken } from '../auth/session-token';
import { logger } from '../lib/logger';
import { useScopeStore } from '../store/scope-store';

/**
 * Thin fetch wrapper for the API.
 *
 * Relative to `/api` by default, so the Vite dev proxy (and Firebase
 * Hosting's rewrite, in any environment still using it) handles routing.
 * `apiOrigin` below overrides this per-environment for anything that has cut
 * over to reaching Cloud Run directly through a load balancer instead.
 */
export class ApiError extends Error {
  // Declared explicitly rather than as constructor parameter properties: the
  // vite tsconfig preset sets erasableSyntaxOnly, which forbids syntax that
  // emits code.
  readonly status: number;

  /**
   * The API's own machine-readable reason, when it sent one — `code` on the
   * error body (`INSUFFICIENT_CREDITS`, …).
   *
   * Present so a caller can branch on *why* rather than matching the message
   * text, which is prose written for a human and is free to change. A 400 with
   * no code stays undefined; most of them have none.
   */
  readonly code?: string;

  /** The machine-readable particulars that go with `code` — for
   *  `INSUFFICIENT_CREDITS`, `{ available, required }`. */
  readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** What a refusal for want of credits said it needed and found, when it
 *  said — null for any other error. */
export function creditShortfall(
  err: unknown,
): { available: number; required: number } | null {
  if (!isInsufficientCredits(err)) return null;
  const details = (err as ApiError).details;
  const available = details?.available;
  const required = details?.required;
  return typeof available === 'number' && typeof required === 'number'
    ? { available, required }
    : null;
}

/** True when the API refused because the account cannot afford the run. The
 *  one error the product has a considered screen for, so it is worth naming
 *  once here rather than spelling the string at each panel. */
export function isInsufficientCredits(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'INSUFFICIENT_CREDITS';
}

const log = logger.child({ module: 'api-client' });

/**
 * Where a *streaming* request goes, which is not where everything else goes.
 *
 * Every ordinary call stays on the relative `/api/*` path, so the Vite proxy
 * handles it in dev and Firebase Hosting's rewrite handles it in production —
 * no per-environment base URL, which is the whole reason the session is a
 * bearer token rather than a cookie.
 *
 * **Server-sent events are the exception, because Hosting will not carry
 * them.** Measured against the deployed test site: straight to Cloud Run, the
 * first frame lands in 0.4s and heartbeats follow; through the Hosting rewrite,
 * thirty seconds and not one byte. The CDN buffers a streaming response instead
 * of passing it through, so the client saw a connection that opened, delivered
 * nothing, and was eventually cut — then reconnected, roughly once a second,
 * forever. Cloud Run logged each one as "Truncated response body".
 *
 * Nothing about the API is wrong: it already answers with `x-accel-buffering:
 * no` and `cache-control: no-transform`. There is no header that makes Hosting
 * stream, so the stream goes around it.
 *
 * Cross-origin is safe here for the same reason the bearer token exists: no
 * cookie is involved, so there is no `SameSite` policy to satisfy and no
 * credentialed CORS to configure — the API's bare `enableCors()` already
 * answers the preflight.
 *
 * Unset means same-origin, so local dev is unchanged and a deployment that
 * forgets it degrades to what the app did before realtime — a correct first
 * paint that simply stops updating — rather than breaking. Deployments must set
 * it; `deploy-web-test.yml` defaults it in the workflow rather than trusting a
 * repo variable, the lesson `VITE_MCP_URL` left behind.
 */
export const streamOrigin: string =
  (import.meta.env.VITE_API_STREAM_ORIGIN as string | undefined) ?? '';

/**
 * Where an *ordinary* API call goes once Cloud Run sits behind the Cloud
 * Armor-fronted load balancer instead of Firebase Hosting's `run:` rewrite.
 *
 * Locking a Cloud Run service's ingress to load-balancer-only traffic (the
 * point of putting it behind Cloud Armor at all) breaks Hosting's rewrite —
 * Google's own docs say the rewrite requires the default `run.app` URL and
 * `ingress: all`. So the same cross-origin move `streamOrigin` already made
 * for SSE applies here too, for the same reason: no cookie is involved, so
 * there's no `SameSite` policy to satisfy and no credentialed CORS to
 * configure.
 *
 * Unset means same-origin, so any environment that hasn't cut over to the
 * load balancer (today: every environment) is completely unaffected.
 */
export const apiOrigin: string =
  (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';

/**
 * What the API said, when it said anything.
 *
 * Nest's ValidationPipe answers `{ message: ['dpr must not be less than 1'] }`
 * and its exceptions `{ message: 'Flow not found' }` — the one part of a 4xx
 * that names the cause. Throwing only "Request to /canvas/captures failed with
 * 400" meant every validation failure looked identical from the client, and a
 * capture rejected for a fractional device-pixel-ratio was indistinguishable
 * from one rejected for its URL.
 */
export async function failure(
  res: Response,
  path: string,
  fallback = `Request to ${path} failed`,
): Promise<{
  code?: string;
  details?: Record<string, unknown>;
  message: string;
}> {
  try {
    const body = (await res.clone().json()) as {
      code?: unknown;
      details?: unknown;
      message?: unknown;
    } | null;
    const code = typeof body?.code === 'string' ? body.code : undefined;
    const details =
      body?.details && typeof body.details === 'object'
        ? (body.details as Record<string, unknown>)
        : undefined;
    const message = body?.message;

    if (typeof message === 'string' && message) {
      return { code, details, message };
    }
    if (Array.isArray(message) && message.length > 0) {
      return { code, details, message: message.map(String).join(', ') };
    }
    return { code, details, message: fallback };
  } catch {
    // Not JSON, or a body that was already consumed. The path still says which
    // call failed, which is what this fell back to before.
    return { message: fallback };
  }
}

export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const method = init?.method ?? 'GET';
  const startedAt = performance.now();

  try {
    const sessionToken = getSessionToken();
    const teamId = useScopeStore.getState().selectedTeamId;

    const res = await fetch(`${apiOrigin}/api${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
        // Personal scope sends no header at all — the API already treats a
        // missing one as "act as my own account", which is also what every
        // pre-existing caller (extension, older clients) has always sent.
        ...(teamId ? { 'x-team-id': teamId } : {}),
        ...init?.headers,
      },
      ...init,
    });

    const durationMs = Math.round(performance.now() - startedAt);

    if (!res.ok) {
      // warn, not error: a 4xx is usually the user or the caller, not a fault.
      log.warn(
        { durationMs, method, path, status: res.status },
        'request failed',
      );

      const { code, details, message } = await failure(res, path);
      throw new ApiError(
        `${message} (${String(res.status)})`,
        res.status,
        code,
        details,
      );
    }

    log.debug({ durationMs, method, path, status: res.status }, 'request ok');

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } catch (err) {
    // ApiError was already logged above; anything else is a genuine transport or
    // parsing fault worth an error line with the stack attached.
    if (!(err instanceof ApiError)) {
      log.error(
        {
          durationMs: Math.round(performance.now() - startedAt),
          err,
          method,
          path,
        },
        'request threw',
      );
    }

    throw err;
  }
}

/**
 * A multipart POST — a file and its fields.
 *
 * Separate from `apiFetch` rather than an option on it: `apiFetch` sets
 * `Content-Type: application/json` on every call, and a multipart body must
 * have *no* Content-Type from us, so the browser can write the boundary into
 * it. Same origin, same bearer token, same error shape.
 */
export async function apiUpload<T>(path: string, body: FormData): Promise<T> {
  const sessionToken = getSessionToken();
  const teamId = useScopeStore.getState().selectedTeamId;

  const res = await fetch(`${apiOrigin}/api${path}`, {
    body,
    headers: {
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      ...(teamId ? { 'x-team-id': teamId } : {}),
    },
    method: 'POST',
  });

  if (!res.ok) {
    log.warn({ method: 'POST', path, status: res.status }, 'upload failed');
    const { code, details, message } = await failure(res, path);
    throw new ApiError(
      `${message} (${String(res.status)})`,
      res.status,
      code,
      details,
    );
  }

  return (await res.json()) as T;
}
